import { create } from 'zustand';
import { Property, Client, Match, Notification } from '@/types';

interface AppState {
  // UI State
  sidebarOpen: boolean;
  theme: 'light' | 'dark' | 'auto';
  loading: boolean;
  error: string | null;
  
  // Data State
  properties: Property[];
  clients: Client[];
  matches: Match[];
  notifications: Notification[];
  
  // Filters and Search
  propertyFilters: any;
  clientFilters: any;
  searchTerm: string;
  
  // Pagination
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  
  // Actions
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'auto') => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  setProperties: (properties: Property[]) => void;
  addProperty: (property: Property) => void;
  updateProperty: (id: string, updates: Partial<Property>) => void;
  removeProperty: (id: string) => void;
  
  setClients: (clients: Client[]) => void;
  addClient: (client: Client) => void;
  updateClient: (id: string, updates: Partial<Client>) => void;
  removeClient: (id: string) => void;
  
  setMatches: (matches: Match[]) => void;
  addMatch: (match: Match) => void;
  updateMatch: (id: string, updates: Partial<Match>) => void;
  removeMatch: (id: string) => void;
  
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markNotificationAsRead: (id: string) => void;
  removeNotification: (id: string) => void;
  
  setPropertyFilters: (filters: any) => void;
  setClientFilters: (filters: any) => void;
  setSearchTerm: (term: string) => void;
  
  setPagination: (page: number, itemsPerPage: number, totalItems: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  
  reset: () => void;
}

export const useAppStore = create<AppState>((set) => ({
  // Initial State
  sidebarOpen: true,
  theme: 'auto',
  loading: false,
  error: null,
  
  properties: [],
  clients: [],
  matches: [],
  notifications: [],
  
  propertyFilters: {},
  clientFilters: {},
  searchTerm: '',
  
  currentPage: 1,
  itemsPerPage: 12,
  totalItems: 0,
  
  // UI Actions
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setTheme: (theme) => set({ theme }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  
  // Property Actions
  setProperties: (properties) => set({ properties }),
  addProperty: (property) => set((state) => ({ 
    properties: [...state.properties, property] 
  })),
  updateProperty: (id, updates) => set((state) => ({
    properties: state.properties.map(property =>
      property.id === id ? { ...property, ...updates } : property
    )
  })),
  removeProperty: (id) => set((state) => ({
    properties: state.properties.filter(property => property.id !== id)
  })),
  
  // Client Actions
  setClients: (clients) => set({ clients }),
  addClient: (client) => set((state) => ({ 
    clients: [...state.clients, client] 
  })),
  updateClient: (id, updates) => set((state) => ({
    clients: state.clients.map(client =>
      client.id === id ? { ...client, ...updates } : client
    )
  })),
  removeClient: (id) => set((state) => ({
    clients: state.clients.filter(client => client.id !== id)
  })),
  
  // Match Actions
  setMatches: (matches) => set({ matches }),
  addMatch: (match) => set((state) => ({ 
    matches: [...state.matches, match] 
  })),
  updateMatch: (id, updates) => set((state) => ({
    matches: state.matches.map(match =>
      match.id === id ? { ...match, ...updates } : match
    )
  })),
  removeMatch: (id) => set((state) => ({
    matches: state.matches.filter(match => match.id !== id)
  })),
  
  // Notification Actions
  setNotifications: (notifications) => set({ notifications }),
  addNotification: (notification) => set((state) => ({ 
    notifications: [...state.notifications, notification] 
  })),
  markNotificationAsRead: (id) => set((state) => ({
    notifications: state.notifications.map(notification =>
      notification.id === id ? { ...notification, read: true } : notification
    )
  })),
  removeNotification: (id) => set((state) => ({
    notifications: state.notifications.filter(notification => notification.id !== id)
  })),
  
  // Filter and Search Actions
  setPropertyFilters: (filters) => set({ propertyFilters: filters }),
  setClientFilters: (filters) => set({ clientFilters: filters }),
  setSearchTerm: (term) => set({ searchTerm: term }),
  
  // Pagination Actions
  setPagination: (page, itemsPerPage, totalItems) => 
    set({ currentPage: page, itemsPerPage, totalItems }),
  nextPage: () => set((state) => {
    const maxPage = Math.ceil(state.totalItems / state.itemsPerPage);
    return { 
      currentPage: Math.min(state.currentPage + 1, maxPage) 
    };
  }),
  prevPage: () => set((state) => ({ 
    currentPage: Math.max(state.currentPage - 1, 1) 
  })),
  
  // Reset Action
  reset: () => set({
    sidebarOpen: true,
    theme: 'auto',
    loading: false,
    error: null,
    properties: [],
    clients: [],
    matches: [],
    notifications: [],
    propertyFilters: {},
    clientFilters: {},
    searchTerm: '',
    currentPage: 1,
    itemsPerPage: 12,
    totalItems: 0,
  }),
}));
